import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { TabList, TabsModule } from 'primeng/tabs';
import { CountLabelComponent } from '../../labels/count-label.component';
import { TabComponent } from './tab.component';
import { CaseType, CasePipe } from '../../../../pipes/case.pipe';
@Component({
  selector: 'app-tab-panel',
  imports: [
    MenuModule,
    CommonModule,
    TabsModule,
    ReactiveFormsModule,
    RouterModule,
    CountLabelComponent,
    CasePipe,
  ],
  template: `
    <div [class]="classes">
      <p-tabs
        (valueChange)="onValueChange($event)"
        scrollable
        [value]="value"
        [lazy]="lazy"
      >
        <div
          class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-[var(--p-content-border-color)] tab-header-row"
        >
          <div class="tw-flex-1 tw-min-w-0">
            <p-tablist>
              @for (tab of tabs; track tab) {
                @if (tab.visible) {
                  <p-tab
                    [value]="tab.computedValue"
                    [routerLink]="tab.routerLink"
                    role="tab"
                  >
                    <div class="tw-flex tw-items-center tw-gap-1.5">
                      @if (tab.icon) {
                        <i [class]="tab.icon" class="tw-text-lg tw-pr-1"></i>
                      }
                      <span class="tw-whitespace-nowrap">{{
                        tab.label | case: labelCase
                      }}</span>
                      @if (tab.count > 0) {
                        <app-count-label [label]="tab.count" />
                      }
                    </div>
                  </p-tab>
                }
              }
            </p-tablist>
          </div>
          <div class="tw-flex-shrink-0">
            <ng-content select="[tabPanelRight]"></ng-content>
          </div>
        </div>
        <p-tabpanels>
          @for (tab of tabs; track tab.computedValue) {
            <p-tabpanel [value]="tab.computedValue" role="tabpanel">
              @defer (on viewport) {
                @if (tab.visible) {
                  <ng-container
                    *ngTemplateOutlet="tab.contentTemplate"
                  ></ng-container>
                }
              } @placeholder {
                <div></div>
              }
            </p-tabpanel>
          }
        </p-tabpanels>
      </p-tabs>
    </div>
  `,
})
export class TabPanelComponent
  implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy
{
  @ContentChildren(TabComponent, { descendants: false })
  tabs: QueryList<TabComponent>;

  @ViewChild(TabList)
  private tabList?: TabList;

  /**
   * Activates a tab. Must match tab's value property.
   */
  @Input()
  value: string;

  /**
   * Lazy load tab content on tab activate. Default is true.
   */
  @Input()
  lazy = true;

  @Input()
  classes: string;

  @Input()
  labelCase: CaseType = 'none';

  /**
   * When set, the active tab value is synced to the URL as a query param with this name (e.g. "tab" -> ?tab=history).
   */
  @Input()
  activeTabQueryParam: string | null = null;

  @Output()
  valueChange = new EventEmitter<any>();

  protected radioTab = new FormControl<string>(null);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;

  protected onValueChange(newValue: string | number): void {
    const value = String(newValue);
    if (this.activeTabQueryParam) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { [this.activeTabQueryParam]: value },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.valueChange.emit(value);
    this.scheduleScrollActiveTabIntoView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.scheduleScrollActiveTabIntoView();
    }
  }

  ngAfterContentInit(): void {
    // Activate the first tab by default.
    if (!this.value && this.tabs?.first) {
      this.value = this.tabs.first.computedValue;
    }
  }

  ngAfterViewInit(): void {
    // Reposition PrimeNG's active-bar when the panel becomes visible or reflows;
    // its one-shot measurement reads width 0 if rendered while hidden.
    // Also scroll the active tab into the narrow viewport (mobile deep-links).
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            (entry.target as HTMLElement).offsetWidth > 0
          ) {
            this.repositionInkBar();
            this.scheduleScrollActiveTabIntoView();
          }
        }
      });
      this.intersectionObserver.observe(this.host.nativeElement);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.host.nativeElement.offsetWidth > 0) {
          this.repositionInkBar();
          this.scrollActiveTabIntoView();
        }
      });
      this.resizeObserver.observe(this.host.nativeElement);
    }

    this.scheduleScrollActiveTabIntoView();
  }

  /**
   * Wait for PrimeNG to mark the active tab before measuring scroll position.
   */
  private scheduleScrollActiveTabIntoView(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      setTimeout((): void => this.scrollActiveTabIntoView());
      return;
    }
    requestAnimationFrame(() => {
      this.scrollActiveTabIntoView();
      requestAnimationFrame(() => this.scrollActiveTabIntoView());
    });
  }

  /**
   * When the tab strip overflows (mobile), center the focused tab in the viewport
   * so it clears the blur/fade near the scroll nav buttons.
   * PrimeNG only scrolls on keyboard focus — not when value is set via route/input.
   */
  private scrollActiveTabIntoView(): void {
    const root = this.host.nativeElement;
    if (root.offsetWidth <= 0) {
      return;
    }

    const activeTab = root.querySelector(
      '.p-tablist [data-p-active="true"]',
    ) as HTMLElement | null;
    const scrollContainer = root.querySelector(
      '.p-tablist-content',
    ) as HTMLElement | null;

    if (!activeTab || !scrollContainer) {
      return;
    }

    const tabRect = activeTab.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const delta =
      tabRect.left +
      tabRect.width / 2 -
      (containerRect.left + containerRect.width / 2);
    const maxScrollLeft = Math.max(
      0,
      scrollContainer.scrollWidth - scrollContainer.clientWidth,
    );

    scrollContainer.scrollLeft = Math.min(
      Math.max(0, scrollContainer.scrollLeft + delta),
      maxScrollLeft,
    );

    this.repositionInkBar();
    this.tabList?.updateButtonState();
  }

  private repositionInkBar(): void {
    this.tabList?.updateInkBar();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
  }
}
